import { createRouter, createWebHistory } from 'vue-router'
import axios from 'axios'
import { setupNavigationGuards } from './guards'

const routes = [
  // Authentication
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/Login.vue'),
    meta: { public: true }
  },
  {
    path: '/mfa',
    name: 'MFA',
    component: () => import('../views/MFA.vue'),
    meta: { public: true }
  },

  // Main Dashboard - dynamically serve appropriate dashboard
  {
    path: '/',
    name: 'Dashboard',
    component: () => {
      // Check user type to determine which dashboard to serve
      const token = localStorage.getItem('b2b_token') || localStorage.getItem('token')
      if (token) {
        try {
          const tokenPayload = JSON.parse(atob(token.split('.')[1]))
          const clientType = tokenPayload.clientType
          
          // B2B customers get their own dashboard
          if (clientType?.toLowerCase() === 'b2b') {
            return import('../views/CustomerDashboard.vue')
          }
        } catch (error) {
          console.warn('Failed to decode token for dashboard routing:', error)
        }
      }
      
      // Internal users (admin, sales, etc.) get the full dashboard
      return import('../views/Dashboard.vue')
    }
  },

  // Ordering Section
  {
    path: '/products',
    name: 'ProductsCatalog',
    component: () => import('../views/Products.vue')
  },
  {
    path: '/cart',
    name: 'Cart',
    component: () => import('../views/Cart.vue')
  },
  {
    path: '/checkout',
    name: 'Checkout',
    component: () => import('../views/Checkout.vue')
  },
  {
    path: '/draft-orders',
    name: 'DraftOrders',
    component: () => import('../views/DraftOrders.vue')
  },
  {
    path: '/permission-test',
    name: 'PermissionTest',
    component: () => import('../views/PermissionTest.vue')
  },
  {
    path: '/security-test',
    name: 'SecurityTest',
    component: () => import('../views/SecurityTest.vue')
  },
  {
    path: '/auth-test',
    name: 'AuthTest',
    component: () => import('../views/AuthTest.vue')
  },
  {
    path: '/templates',
    name: 'OrderTemplates',
    component: () => import('../views/OrderTemplates.vue')
  },
  {
    path: '/sales-order-entry',
    name: 'SalesOrderEntry',
    component: () => import('../views/SalesOrderEntry.vue'),
    meta: {
      requiresPermission: 'b2b_orders.create_for_client',
      internalOnly: true
    }
  },

  // Account Section  
  {
    path: '/orders',
    name: 'OrderHistory',
    component: () => import('../views/SalesOrders.vue')
  },
  
  // Order Management (for internal users - same component as orders)
  {
    path: '/order-management',
    name: 'OrderManagement',
    component: () => import('../views/SalesOrders.vue'),
    meta: {
      requiresPermission: 'b2b_orders.view_all',
      internalOnly: true
    }
  },

  // Credit Management (Internal Users Only)
  {
    path: '/credit-management',
    name: 'CreditManagement',
    component: () => import('../views/CreditManagement.vue'),
    meta: {
      requiresPermission: 'b2b_credit.view',
      internalOnly: true
    }
  },

  // Invoices & Payments
  {
    path: '/invoices',
    name: 'Invoices',
    component: () => import('../views/Invoices.vue'),
    meta: {
      requiresPermission: 'b2b_orders.view_all',
      internalOnly: true
    }
  },

  // User Profile
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('../views/Profile.vue')
  },

  // User Management (Internal Users Only)
  {
    path: '/users',
    name: 'UserManagement',
    component: () => import('../views/UserManagement.vue'),
    meta: {
      requiresPermission: 'b2b_teams.manage',
      internalOnly: true
    }
  },

  // Client Directory (Internal Users Only)
  {
    path: '/clients',
    name: 'ClientDirectory',
    component: () => import('../views/Clients.vue'),
    meta: {
      requiresPermission: 'b2b_clients.view',
      internalOnly: true
    }
  },

  // TODO: Add these routes when views are created
  // - /order-management (OrderManagement.vue) 
  // - /invoices (Invoices.vue)
  // - /pricing (PriceLists.vue)
  // - /reports (SalesReports.vue)
  // - /analytics (Analytics.vue)
  // - /team (TeamManagement.vue)
  // - /settings (SystemSettings.vue)
  // - /quick-order (QuickOrder.vue)
  // - /credit (CreditStatus.vue)
  // - /company/profile (CompanyProfile.vue)
  // - /company/team (CompanyTeam.vue)
  // - /company/addresses (CompanyAddresses.vue)
  // - /contact (ContactSupport.vue)
  // - /help (HelpCenter.vue)

  // 404 catch-all - redirect to login or home based on auth status
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    redirect: to => {
      const token = localStorage.getItem('b2b_token') || localStorage.getItem('token')
      if (token) {
        // User is logged in, redirect to home
        return '/'
      } else {
        // User is not logged in, redirect to login
        return '/login'
      }
    }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

import { useAuthStore } from '@/stores/auth'

// Set up comprehensive navigation guards
setupNavigationGuards(router)

// Legacy B2B Navigation guard - DISABLED (using new guard system)
/*
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const token = localStorage.getItem('b2b_token') || localStorage.getItem('token')
  const user = localStorage.getItem('b2b_user') || localStorage.getItem('user')
  
  // Set auth header if token exists
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
    
    // Decode JWT to get actual client type
    try {
      const tokenPayload = JSON.parse(atob(token.split('.')[1]))
      const actualClientType = tokenPayload.clientType
      if (actualClientType) {
        axios.defaults.headers.common['X-Client-Type'] = actualClientType
      }
    } catch (error) {
      console.warn('Failed to decode token in router:', error)
    }
  }
  
  // Public routes don't require auth
  if (to.meta.public) {
    // If already logged in, redirect to dashboard
    if (token && (to.path === '/login' || to.path === '/')) {
      return next('/')
    }
    return next()
  }
  
  // All other routes require B2B authentication
  if (!token) {
    return next('/login')
  }
  
  // Verify B2B user permissions
  if (user) {
    try {
      const userData = JSON.parse(user)
      
      // Manager routes require manager role
      if (to.meta.requiresManager) {
        if (!['manager', 'admin'].includes(userData.role)) {
          return next('/')
        }
      }
      
      // Check permission-based access
      if (to.meta.requiresPermission) {
        // Ensure auth store is initialized
        if (!authStore.isAuthenticated) {
          authStore.initializeAuth()
        }
        
        const hasPermission = authStore.hasPermission(to.meta.requiresPermission)
        
        if (!hasPermission) {
          console.warn(`Access denied: Missing permission ${to.meta.requiresPermission}`)
          // Redirect to dashboard with error message
          return next({
            path: '/dashboard',
            query: { error: 'permission_denied' }
          })
        }
      }
      
      // Check if route is for internal users only
      if (to.meta.internalOnly) {
        const isInternal = userData.clientType === 'internal' || 
                          userData.clientType === 'admin'
        
        if (!isInternal) {
          console.warn('Access denied: Internal users only')
          return next('/dashboard')
        }
      }
      
      // Check client status (active, credit approved, etc.)
      if (userData.client && userData.client.status === 'suspended') {
        // Redirect to a suspended account page
        return next('/suspended')
      }
      
    } catch (error) {
      console.error('Failed to parse user data:', error)
      localStorage.removeItem('b2b_token')
      localStorage.removeItem('b2b_user')
      return next('/login')
    }
  }
  
  next()
})
*/

// Add request interceptor for B2B API calls
axios.interceptors.request.use(
  (config) => {
    // Ensure B2B API base URL
    if (!config.baseURL && !config.url.startsWith('http')) {
      config.baseURL = import.meta.env.VITE_API_URL || 'https://api.banjinos.dev'
    }
    
    // Add client type header from JWT token if available
    const token = config.headers.Authorization?.replace('Bearer ', '') || 
                  axios.defaults.headers.common['Authorization']?.replace('Bearer ', '')
    
    if (token) {
      try {
        const tokenPayload = JSON.parse(atob(token.split('.')[1]))
        const actualClientType = tokenPayload.clientType
        if (actualClientType) {
          config.headers['X-Client-Type'] = actualClientType
        }
      } catch (error) {
        // Fallback to global axios header if set
        if (axios.defaults.headers.common['X-Client-Type']) {
          config.headers['X-Client-Type'] = axios.defaults.headers.common['X-Client-Type']
        }
      }
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor for B2B error handling
axios.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    if (error.response?.status === 401) {
      // Handle B2B authentication errors
      localStorage.removeItem('b2b_token')
      localStorage.removeItem('b2b_user')
      router.push('/login')
    } else if (error.response?.status === 403) {
      // Handle B2B authorization errors
      router.push('/unauthorized')
    }
    
    return Promise.reject(error)
  }
)

export default router